import React, { useContext, useEffect, useState } from 'react';
import { TABTestItem } from '../../types/ab';
import { TPaginationParams } from 'Pages/GMS/Performance/analytics';
import { createAbTest, deleteABTest, getABTests } from '../../services/ABServices';
import { AppContext } from 'Context/AppContext';
import ABTable from '../../components/ABTable';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import DeleteProjectModal from 'Pages/GMS/ISR/Components/DeleteProjectModal';
import { CREATE_AB_TEST, GMS_AB_TESTING_CREATE, GMS_AB_TESTING_DELETE, GMS_AB_TESTING_READ, GMS_AB_TESTING_UPDATE, TEST_NAME } from '../../utils/Constants';
import { useNavigate, useLocation } from "react-router";
import ABHomeHeader from './ABHomeHeader';
import { ABContext } from '../../contexts/ABContext';
import CreateABTestDialog from '../../components/CreateABTestDialog';
import Button from 'Components/DesignSystem/Button/Button';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import EmptyState from 'Components/DesignSystem/EmptyState/EmptyState';
import NoDataIllustration from 'Components/DesignSystem/EmptyState/NoDataIllustration';
import Pagination from 'Components/DesignSystem/Pagination/Pagination';
import Typography from 'Components/DesignSystem/Typography/Typography';

/**
 * The A/B test list (`okf/design-system/e2e.md` § 142).
 *
 * Every fetch, permission gate and the 60s auto-refresh are untouched. What moved is
 * the page furniture: `TableSkeletonElement` -> the DS Table's own `loading` treatment,
 * `NoDataComponent` -> `EmptyState`, and the raw `<select>` + `PaginatorWithApi` pair
 * -> `Dropdown` + `Pagination`. All three legacy components stay in the repo; 15, 18
 * and 9 files respectively still import them.
 */
const PAGE_SIZE_OPTIONS = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
];

const ABHomePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const appContext = useContext(AppContext);
    const abContext = useContext(ABContext);
    const industryId = appContext.get_selected_industry() && appContext?.get_selected_industry()?.id;
    const channelKey = appContext?.channelKey
    const [abTestData, setAbTestData] = useState<TABTestItem[]>([]);
    const [paginationParams, setPaginationParams] = useState<TPaginationParams>({ pageNumber: 1, pageSize: 10 });
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [deleteItem, setDeleteItem] = useState<{ id: string, name: string }>({ id: '', name: '' });

    const [showConfirmDialog, setDeleteTest] = useState<boolean>(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [showIndicator, setShowIndicator] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const readPermission = appContext.pm.checkPermission(GMS_AB_TESTING_READ);
    const createPermission = appContext.pm.checkPermission(GMS_AB_TESTING_CREATE);
    const updatePermission = appContext.pm.checkPermission(GMS_AB_TESTING_UPDATE);
    const deletePermission = appContext.pm.checkPermission(GMS_AB_TESTING_DELETE);

    const openDialog = () => {
        if (!createPermission) { navigate("/workspace/module/permission/denied"); return; }
        setShowDialog(true);
    }
    const closeDialog = () => setShowDialog(false);

    useEffect(() => {
        if ((location.state as any)?.openCreate && createPermission) {
            openDialog();
            navigate({
  pathname: location.pathname
}, {
  state: {},
  replace: true
});
        }
    }, []);

    useEffect(() => {
        if (!readPermission) { navigate("/workspace/module/permission/denied"); return; }
        if (industryId && channelKey) {
            getAllTests();
            const intervalId = setInterval(getAllTests, 60 * 1000);
            return () => clearInterval(intervalId);
        }
    }, [industryId, channelKey, paginationParams]);

    const getAllTests = async () => {
        setShowLoader(true);
        try {
            const responseData = await getABTests(industryId ?? '', appContext?.channelKey ?? 'test', paginationParams);
            if (responseData?.done) {
                setTotalRecords(responseData?.total_tests ?? 10);
                const abTests = responseData?.data ?? [];
                if (abTests.length === 0 && paginationParams?.pageSize === 20 && paginationParams?.pageNumber > 1) {
                    setPaginationParams(prevState => ({
                        ...prevState,
                        pageNumber: 1
                    }));
                    return;
                }
                setAbTestData(abTests);

            } else {
                toast.error(responseData?.message)
            }
        } catch (error) {
            console.log(error, 'Error');
        } finally {
            setShowLoader(false);
        }
    }

    const createABTest = async (testName: string) => {
        abContext.clearData();
        try {
            setShowIndicator(true);
            const responseData = await createAbTest(industryId ?? '', appContext?.channelKey, testName);
            if (responseData?.done) {
                toast.success(responseData?.message ?? "A/B test created successfully");
                navigateToDetailPage(responseData?.data?.name ?? '', responseData?.data?.id ?? '');
            } else {
                toast.error(responseData?.message ?? '');
            }
        } catch (error) {
            console.log("error:", error)
        } finally {
            setShowIndicator(false);
        }
    }

    const deleteTestData = async () => {
        if (!deleteItem?.id) {
            toast.info('Test id is missing');
            return;
        }
        setDeleteLoading(true);
        try {
            const responseData = await deleteABTest(deleteItem?.id ?? '');
            if (responseData?.done) {
                toast.success(responseData?.message);
                setPaginationParams({ pageNumber: 1, pageSize: 10 });
            } else {
                toast.error(responseData?.message);
            }
        } catch (error) {
            console.log('Error', error);
        } finally {
            setDeleteLoading(false);
            setDeleteTest(false);
        }
    }

    const closeDeleteModal = () => {
        setDeleteTest(false);
    };

    const handlePageChange = (pageNumber: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageNumber: pageNumber,
        }));
    }

    const updatePageSize = (pageSize: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageSize: pageSize,
        }));
    };

    const navigateToDetailPage = (name: string, testId: string = '') => {
        if (!updatePermission) { navigate("/workspace/module/permission/denied"); return; }
        navigate(`/gms/ab/detail/${testId}`);
    }

    return (
        <div id="ab-test-page" data-testid="ab-page">
            <ABHomeHeader onOpen={openDialog} hasCreatePermission={createPermission} />

            {/*
              Loading is the TABLE's state now, not a sibling element.

              `TableSkeletonElement` rendered a full-width block ABOVE where the table
              would appear, so a refetch (the 60s poll) replaced the grid with a grey
              slab and the page jumped. The DS Table draws skeleton rows in its own
              columns on first load and dims + shows a "Refreshing" chip when it already
              has data — which is the difference between "loading" and "reloading" that
              a polling list actually needs.
            */}
            {showLoader || abTestData?.length > 0 ? (
                <ABTable
                    abTests={abTestData}
                    setDeleteItem={setDeleteItem}
                    setDeleteTest={setDeleteTest}
                    deletePermission={deletePermission}
                    updatePermission={updatePermission}
                    loading={showLoader}
                />
            ) : (
                <div id="ab-test-empty-state">
                    {/*
                      `NoDataComponent` rendered the literal string "No Data Found" with
                      no subtitle and no action — a dead end on a page whose whole
                      purpose is the button in the header. The empty state now names the
                      thing that is missing and offers the action, gated by the same
                      `createPermission` the header button uses.
                    */}
                    <EmptyState
                        illustration={<NoDataIllustration />}
                        title="No A/B tests yet"
                        description="Create a test to compare two models on live traffic and see which performs better."
                        size="md"
                        data-testid="ab-empty-message"
                        action={createPermission ? (
                            <Button
                                variant="primary"
                                onClick={openDialog}
                                data-testid="ab-empty-create-btn"
                            >
                                {CREATE_AB_TEST}
                            </Button>
                        ) : undefined}
                    />
                </div>
            )}

            {!showLoader && abTestData?.length > 0 ?
                <div id="ab-test-pagination" className="tw-py-2">
                    <div className="tw-flex tw-justify-end tw-items-center tw-gap-3 tw-flex-wrap">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">Show</Typography>
                            {/*
                              The raw `<select>` had `aria-label` but no visible label
                              associated with it, and an inline `style={{width:'80px'}}`.
                              Its testid is kept — specs key off it.

                              `usePortal` because this control sits at the BOTTOM of the
                              page: without it the listbox renders `absolute` inside the
                              pagination row and is clipped by the page's scroll
                              container, so "20" was cut in half. Portalled, it escapes
                              the container AND `calculatePosition` flips it upward when
                              there is under 260px below — which is always, here.
                            */}
                            <Dropdown
                                label=""
                                aria-label="Records on page select"
                                size="sm"
                                usePortal
                                className="tw-w-[84px]"
                                options={PAGE_SIZE_OPTIONS}
                                value={paginationParams?.pageSize ?? 10}
                                onChange={(value) => updatePageSize(Number(value))}
                                id="ab-test-select-page-size"
                                data-testid="home-abhomepage-select-1"
                            />
                        </div>
                        <Pagination
                            page={paginationParams?.pageNumber}
                            totalItems={totalRecords}
                            pageSize={paginationParams?.pageSize}
                            onPageChange={handlePageChange}
                            size="sm"
                            id="ab-test-paginator"
                            data-testid="ab-pagination"
                        />
                    </div>
                </div>
                : null}
            {showDialog ? <CreateABTestDialog id="ab-test-create-dialog" dialogHeader={CREATE_AB_TEST} labelName={TEST_NAME} onClose={closeDialog} onSubmodelFieldSubmit={createABTest} showIndicator={showIndicator} message='Creating AB test ...' /> : null}
            <DeleteProjectModal id="ab-test-delete-modal" showModal={showConfirmDialog} closeModal={closeDeleteModal} loading={deleteLoading} onConfirm={deleteTestData} deleteItem={deleteItem} />
        </div>
    );
}

export default ABHomePage;